import type { ReactElement } from "react";
import { useCallback, useDeferredValue, useMemo, useState } from "react";

import { Combobox, DropdownButton, IconButton } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { StyledBaseDropdownButtonWrap } from "app/pages/sales-intelligence/common-components/styles";
import { buildLocationGroups, getSelectedCodes } from "app/pages/sales-intelligence/utils/location";
import type { ICountryObject } from "app/services/CountryService";
import type { IRegionObject } from "app/services/RegionService";

import { ScrollDismissBoundary } from "../../scroll-dismiss/ScrollDismissBoundary";

import { LocationDropdownOptionsBody } from "./LocationDropdownOptionsBody";
import type { LocationDropdownValue } from "./types";
import { getVisibleRegionGroups, isRegionExpandedWhenSearching } from "./utils";

export type { LocationDropdownValue } from "./types";

export type LocationDropdownProps = {
    regions: readonly IRegionObject[];
    countries: readonly ICountryObject[];
    value: LocationDropdownValue | undefined;
    onChange: (codes: readonly number[]) => void;
    dataAutomation: string;
};

export const LocationDropdown = ({
    regions,
    countries,
    value,
    onChange,
    dataAutomation,
}: LocationDropdownProps): ReactElement => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [expanded, setExpanded] = useState(() => new Set<string>());
    const deferredSearchTerm = useDeferredValue(searchTerm);

    const selectedCodes = useMemo(() => getSelectedCodes(value, regions), [value, regions]);

    const { groups } = useMemo(
        () =>
            buildLocationGroups(regions, countries, translate("si.lead_gen_filters.countries.other_countries_region")),
        [regions, countries],
    );

    const handleOpenChange = useCallback((open: boolean) => {
        setIsOpen(open);
        if (!open) {
            setSearchTerm("");
        }
    }, []);

    const handleClearSearchTerm = useCallback(() => {
        setSearchTerm("");
    }, []);

    const toggleExpanded = useCallback((regionName: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(regionName)) {
                next.delete(regionName);
            } else {
                next.add(regionName);
            }
            return next;
        });
    }, []);

    const visibleGroups = useMemo(
        () => getVisibleRegionGroups(groups, deferredSearchTerm),
        [groups, deferredSearchTerm],
    );

    const searchActive = deferredSearchTerm.trim().length > 0;

    const isRegionExpanded = useCallback(
        (regionName: string) => isRegionExpandedWhenSearching(expanded, searchActive, regionName),
        [expanded, searchActive],
    );

    return (
        <Box
            className="countries_and_regions_dd"
            data-automation={dataAutomation}
            alignSelf="flex-start"
            width="263px"
        >
            <ScrollDismissBoundary
                isOpen={isOpen}
                onClose={() => handleOpenChange(false)}
            >
                <Combobox
                    isOpen={isOpen}
                    onOpenChange={handleOpenChange}
                    placement="bottom-start"
                    width="263px"
                >
                    <Combobox.Trigger>
                        <StyledBaseDropdownButtonWrap>
                            <DropdownButton>{translate("si.lead_gen_filters.countries.dd_placeholder")}</DropdownButton>
                        </StyledBaseDropdownButtonWrap>
                    </Combobox.Trigger>

                    <Combobox.SearchInput
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder={translate("si.lead_gen_filters.companyHeadquarter.dd_placeholder")}
                    />

                    {searchTerm.length > 0 ? (
                        <Flex
                            position="absolute"
                            top="16px"
                            right="10px"
                            zIndex={1}
                            alignItems="center"
                        >
                            <IconButton
                                type="flat"
                                iconName="clear"
                                iconSize="xs"
                                aria-label={translate("common.clear")}
                                dataAutomation={`${dataAutomation}-search-clear`}
                                onClick={handleClearSearchTerm}
                            />
                        </Flex>
                    ) : null}

                    <LocationDropdownOptionsBody
                        visibleGroups={visibleGroups}
                        selectedCodes={selectedCodes}
                        dataAutomation={dataAutomation}
                        searchActive={searchActive}
                        isRegionExpanded={isRegionExpanded}
                        onChange={onChange}
                        toggleExpanded={toggleExpanded}
                    />
                </Combobox>
            </ScrollDismissBoundary>
        </Box>
    );
};
